import { toDollarAmount } from '@fastre/core/helperFunctions/number'
import { capitalize } from '@fastre/core/helperFunctions/string'
import { getCommissionCalculatedPrice, ListingType } from '@fastre/core/schemas/listing'
import {
    InternalSaleListingSchema,
    listingEnabledForUser,
    NewSaleListingSchema,
    UpdateSaleListingSchema,
} from '@fastre/core/schemas/saleListing'
import { VendorType } from '@fastre/core/schemas/vendor'
import { zodResolver } from '@hookform/resolvers/zod'
import { DeleteRounded, EditRounded } from '@mui/icons-material'
import {
    Box,
    Button,
    ButtonGroup,
    Divider,
    FormControl,
    FormLabel,
    IconButton,
    Modal,
    Option,
    Radio,
    RadioGroup,
    Sheet,
    Stack,
    Switch,
    Table,
    Typography,
} from '@mui/joy'
import { useApi } from 'api'
import { useListingConfigApi, useListingsApi, useUsersApi } from 'apiProviders'
import { useUserData } from 'auth'
import Loading from 'components/Loading'
import LocationAutocomplete from 'components/LocationAutocomplete'
import AgentInput from 'components/agentInput'
import Input, { SlotInput, SlotWrapper } from 'components/input'
import Select from 'components/select'
import { useShowSnack } from 'components/snackbar'
import { MobileFriendlyStack } from 'components/stack'
import UnsavedModal from 'components/unsavedModal'
import { addDays, format } from 'date-fns'
import { useIsMobile } from 'helperFunctions/pageSize'
import { useMaybeState } from 'helperFunctions/react'
import { RenderListingValues } from 'listings/additionalValues'
import { useEditMode } from 'listings/editMode'
import FormStepper from 'listings/formStepper'
import { useListingStatusOptions } from 'listings/helpers'
import { useListingContext } from 'listings/listingProvider'
import MarketingItemsModal from 'listings/marketingItemsModal'
import MarketingPackageModal from 'listings/marketingPackageModal'
import RelloMarketingPayment from 'listings/relloMarketingPaymentSection'
import VendorList from 'listings/vendorList'
import { assoc, dissoc, omit, prop, sum } from 'ramda'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import {
    Controller,
    FormProvider,
    SubmitHandler,
    useFieldArray,
    useForm,
    useFormContext,
} from 'react-hook-form'
import { useBlocker, useNavigate } from 'react-router'
import CreateXeroMarketingInvoiceButton from './createXeroMarketingInvoiceButton'
import QueryQldGovDataButton, { QbccInfo } from './queryQldGovDataButton'

const AdditionalChargeItem = ({
    charge,
    index,
    formDisabled,
    removeAdditionalCharge,
}: {
    charge: any
    index: number
    formDisabled: boolean
    removeAdditionalCharge: (index: number) => void
}) => {
    const { control, setValue, watch } = useFormContext()
    const isMobile = useIsMobile()
    const { editMode } = useEditMode()

    const payType = watch(`additionalCharges.${index}.payType`)

    const showLabels = isMobile || index == 0

    return (
        <tr>
            <td>
                <Controller
                    name={`additionalCharges.${index}.description`}
                    disabled={formDisabled}
                    control={control}
                    render={field => (
                        <SlotInput
                            {...field}
                            formControlProps={{ sx: { minWidth: '170px' } }}
                        />
                    )}
                />
            </td>
            <td>
                <Controller
                    name={`additionalCharges.${index}.amount`}
                    disabled={formDisabled}
                    control={control}
                    render={field => (
                        <SlotInput
                            type="dollar"
                            {...field}
                            formControlProps={{ sx: { minWidth: '170px' } }}
                        />
                    )}
                />
            </td>
            <td>
                <Controller
                    name={`additionalCharges.${index}.payType`}
                    control={control}
                    render={field => (
                        <FormControl
                            sx={{
                                gap: 1,
                                paddingTop: index > 0 ? '8px' : '1px',
                                minWidth: '130px',
                            }}
                        >
                            {editMode ? (
                                <Switch
                                    {...dissoc('value', field.field)}
                                    checked={field.field.value == 'settlement'}
                                    onChange={e =>
                                        setValue(
                                            `additionalCharges.${index}.payType`,
                                            e.target.checked ? 'settlement' : 'now',
                                        )
                                    }
                                    startDecorator={({ checked }) => (
                                        <Typography
                                            level="body-sm"
                                            sx={{ opacity: !checked ? 1 : 0.35 }}
                                        >
                                            Date
                                        </Typography>
                                    )}
                                    endDecorator={({ checked }) => (
                                        <Typography
                                            level="body-sm"
                                            sx={{ opacity: checked ? 1 : 0.35 }}
                                        >
                                            Settlement
                                        </Typography>
                                    )}
                                />
                            ) : (
                                <Typography>
                                    {field.field.value == 'settlement' ? 'Settlement' : 'Now'}
                                </Typography>
                            )}
                        </FormControl>
                    )}
                />
            </td>
            <td>
                <Controller
                    name={`additionalCharges.${index}.payable`}
                    disabled={formDisabled}
                    control={control}
                    render={field =>
                        payType == 'settlement' ? (
                            <Input
                                value="On settlement"
                                readOnly
                                disabled
                                formControlProps={{
                                    sx: {
                                        minWidth: '130px',
                                        maxWidth: '150px',
                                        boxSizing: 'border-box',
                                        padding: '0',
                                        margin: '0',
                                    },
                                }}
                            />
                        ) : (
                            <SlotInput
                                type="date"
                                {...field}
                                formControlProps={{ sx: { minWidth: '130px', maxWidth: '150px' } }}
                            />
                        )
                    }
                />
            </td>
            {editMode && (
                <td>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-end',
                        }}
                    >
                        <ButtonGroup>
                            <IconButton
                                variant="outlined"
                                disabled={formDisabled}
                                onClick={() => removeAdditionalCharge(index)}
                            >
                                <DeleteRounded />
                            </IconButton>
                        </ButtonGroup>
                    </Box>
                </td>
            )}
        </tr>
    )
}

const CoreListingDetails = forwardRef(({ type }: { type: ListingType }, ref: any) => {
    const api = useApi()
    const { listing, setListing } = useListingContext()
    const showSnack = useShowSnack()
    const navigate = useNavigate()
    const listingsApi = useListingsApi(false)
    const listingConfigApi = useListingConfigApi()
    const usersApi = useUsersApi(true)
    const listingStatusOptions = useListingStatusOptions(listing)
    const { editMode, setEditMode } = useEditMode()

    const isNew = listing.listingId == undefined
    const { user } = useUserData()

    const [loading, setLoading] = useState(false)
    const [vendorType, setVendorType] = useState<VendorType>(
        listing.vendors ? (listing.vendors[0]?.vendorType ?? 'individual') : 'individual',
    )

    const enabledForUser = listingEnabledForUser(listing, usersApi.data, user)

    const formEnabled =
        isNew ||
        user.permissions.includes('listings.fullControl') ||
        (listing.agentAppointmentForm?.formStatus == 'draft' && enabledForUser)

    const formDisabled = !formEnabled

    const formFullyDisabled =
        !isNew &&
        !user.permissions.includes('listings.fullControl') &&
        !(enabledForUser && user.permissions.includes('listings.edit'))

    const [maybeQbccInfo, setQbccInfo] = useMaybeState<QbccInfo>()
    const [maybePoolCertificate, setPoolCertificate] = useMaybeState<string | boolean>()
    const [showMarketingPackageModal, setShowMarketingPackageModal] = useState(false)
    const [showMarketingItemsModal, setShowMarketingItemsModal] = useState(false)

    const hookForm = useForm<UpdateSaleListingSchema>({
        defaultValues: {
            authorityPeriod: 90,
            listingType: 'sale',
            marketingPaymentMethod: 'rello',
            ...omit(['contract', 'vendors'], listing),
            occupantStatus: (listing as InternalSaleListingSchema).occupantStatus ?? 'owner occupied',
            vendors:
                listing.vendors?.map(({ vendorType, ...v }) => ({
                    ...v,
                    vendorType: vendorType ?? 'individual',
                })) ?? [],
        },
        resolver: zodResolver(isNew ? NewSaleListingSchema : UpdateSaleListingSchema),
        shouldFocusError: true,
    })

    const [ocrLoading, setOcrLoading] = useState(false)

    const { control, handleSubmit, formState, getValues, setValue, setError, trigger, reset, watch } =
        hookForm

    useEffect(() => {
        reset(listing as any)
    }, [listing.listingId])

    const {
        fields: agents,
        append: appendAgent,
        remove: removeAgent,
    } = useFieldArray({
        control,
        name: 'agents',
    })

    const {
        fields: vendors,
        append: appendVendor,
        remove: removeVendor,
    } = useFieldArray({
        control,
        name: 'vendors',
    })

    const {
        fields: additionalCharges,
        append: appendAdditionalCharge,
        remove: removeAdditionalCharge,
    } = useFieldArray({
        control,
        name: 'additionalCharges',
    })

    const agentsWatch = watch('agents')

    useEffect(() => {
        setValue('agents', agentsWatch?.map(assoc('split', 1 / agentsWatch.length)))
    }, [agentsWatch?.map(prop('userId')).join('')])

    const listingType = watch('listingType')
    const authorityStartDate = watch('authorityStartDate')
    const authorityPeriod = watch('authorityPeriod')
    const advertisedPrice = watch('advertisedPrice')
    const commission = watch('commission')
    const searchPriceType = watch('hiddenPrice.type')
    const marketingPackage = watch('marketingPackage')

    const marketingPackageItems = useFieldArray({
        control,
        name: 'marketingPackage.packageItems',
    })

    const occupantStatus = watch('occupantStatus')
    const marketingPaymentMethod = watch('marketingPaymentMethod')
    const hiddenPrice = watch('hiddenPrice')
    const listingAddress = watch('listingAddress')

    useEffect(() => {
        setValue('listingStatus', listing.listingStatus, { shouldDirty: false })
    }, [listing.listingStatus])

    const commissionCalculatedPrice = getCommissionCalculatedPrice({
        advertisedPrice,
        hiddenPrice,
    })

    if (Object.keys(formState.errors).length > 0) {
        console.log('errors', formState.errors)
    }

    useEffect(() => {
        console.log('isDirty', formState.isDirty)
        console.log('dirtyFields', formState.dirtyFields)
    }, [formState.isDirty, JSON.stringify(formState.dirtyFields)])

    const onSubmit: SubmitHandler<UpdateSaleListingSchema> = async data => {
        setLoading(true)
        try {
            if (isNew) {
                const { listingId } = await api.post(`/listing/${type}/create`, data).then(prop('data'))
                navigate(`../../${listingId}`, {
                    relative: 'path',
                    replace: true,
                })
            } else {
                const updatedListing = await api
                    .post(`/listing/${type}/${listing.listingId}/update`, data)
                    .then(prop('data'))
                setListing(updatedListing)
                reset(updatedListing)
            }
            listingsApi.refreshSpecific(listing.listingId)
            showSnack('Listing saved', 'success')
        } catch (e) {
            console.error(e)
            showSnack('Error saving listing', 'danger')
            throw e
        } finally {
            setLoading(false)
        }
    }

    const handleSubmitPromise = (): Promise<boolean> => {
        return new Promise((resolve, reject) => {
            handleSubmit(
                async data => {
                    try {
                        await onSubmit(data)
                        resolve(true)
                    } catch (error) {
                        resolve(false)
                    }
                },
                error => {
                    resolve(false)
                },
            )()
        })
    }

    const saveForm = async (): Promise<boolean> => {
        if (formDisabled) {
            setLoading(true)
            try {
                const updatedListing = await api
                    .post(`/listing/${type}/${listing.listingId}/update`, {
                        ...listing,
                        listingStatus: getValues('listingStatus'),
                        additionalDetails: getValues('additionalDetails'),
                        sensitiveInfo: getValues('sensitiveInfo'),
                    })
                    .then(prop('data'))
                setListing(updatedListing)
                reset(updatedListing)
                listingsApi.refreshSpecific(listing.listingId)
                showSnack('Listing saved', 'success')
                return true
            } catch (e) {
                console.error(e)
                showSnack('Error saving listing', 'danger')
                return false
            } finally {
                setLoading(false)
            }
        } else {
            return await handleSubmitPromise()
        }
    }

    // Expose the `saveForm` method to the parent via ref
    useImperativeHandle(ref, () => ({
        saveForm,
        resetForm: () => reset(listing),
    }))

    const blocker = useBlocker(({ currentLocation, nextLocation }) => {
        //return false
        console.log(formState.dirtyFields)
        console.log('listing', listing.listingId)

        return (
            !isNew &&
            currentLocation.pathname !== nextLocation.pathname &&
            formState.isDirty &&
            Object.keys(formState.dirtyFields).length > 0
        )
    })

    useEffect(() => {
        listingConfigApi.maybeData.forEach(configs => {
            configs.forEach(config =>
                setValue(
                    `additionalDetails.${config.id}`,
                    listing.additionalDetails?.[config.id] ?? config.configValues[0].value,
                ),
            )
        })
    }, [listingConfigApi.maybeData.isSome()])

    if (listingConfigApi.maybeData.isNone()) {
        return <Loading />
    }

    return (
        <Box
            sx={{
                '& hr': {
                    mb: 3,
                },
            }}
        >
            <FormProvider {...hookForm}>
                <form
                    noValidate
                    //onSubmit={handleSubmit(onSubmit)}
                >
                    <Typography
                        level="h4"
                        sx={{ mb: 2 }}
                    >
                        Property Details
                    </Typography>
                    <Stack gap={2}>
                        <Stack
                            gap={2}
                            maxWidth={500}
                        >
                            <LocationAutocomplete
                                name="listingAddress"
                                geoName="geoLocation"
                                control={control}
                                label="Address"
                                slotProps={{
                                    input: {
                                        tabIndex: 0,
                                    },
                                }}
                                autoComplete={false}
                                id="donotautofill"
                                disabled={formDisabled}
                                allowManual
                            />
                            <Controller
                                name="propertyType"
                                control={control}
                                disabled={formDisabled}
                                render={field => (
                                    <FormControl>
                                        <FormLabel>Contract</FormLabel>
                                        {editMode ? (
                                            <RadioGroup
                                                orientation="horizontal"
                                                {...field.field}
                                                onChange={e => field.field.onChange(e.target.value)}
                                            >
                                                <Radio
                                                    value="residential"
                                                    label="House & Land"
                                                    disabled={formDisabled}
                                                />
                                                <Radio
                                                    value="cts"
                                                    label="CTS"
                                                    disabled={formDisabled}
                                                />
                                            </RadioGroup>
                                        ) : (
                                            <Typography>
                                                {field.field.value == 'residential' ? 'House & Land' : 'CTS'}
                                            </Typography>
                                        )}
                                    </FormControl>
                                )}
                            />
                            {editMode && (
                                <Controller
                                    name="listingStatus"
                                    control={control}
                                    disabled={formFullyDisabled}
                                    render={({ field: { ref, ...field } }) => {
                                        return (
                                            <FormControl sx={{ flex: 1 }}>
                                                <FormLabel>Status</FormLabel>
                                                <Select
                                                    {...field}
                                                    onChange={(e, value) => field.onChange(value)}
                                                    slotProps={{
                                                        listbox: {
                                                            ref,
                                                            tabIndex: 0,
                                                        },
                                                    }}
                                                >
                                                    {listingStatusOptions.map(({ value, disabled }) => (
                                                        <Option
                                                            key={value}
                                                            value={value}
                                                            disabled={disabled}
                                                        >
                                                            {capitalize(value)}
                                                        </Option>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )
                                    }}
                                />
                            )}
                            <Controller
                                name="agents.0.userId"
                                control={control}
                                disabled={formDisabled}
                                render={field => (
                                    <SlotWrapper
                                        {...field}
                                        label="Agent"
                                    >
                                        <AgentInput
                                            {...field.field}
                                            //filter={propEq('role', 'sales agent')}
                                        />
                                    </SlotWrapper>
                                )}
                            />
                            <FormControl>
                                <FormLabel>Second Agent</FormLabel>
                                <AgentInput
                                    disableClearable={false}
                                    value={agentsWatch?.[1]?.userId ?? null}
                                    disabled={formDisabled || agentsWatch?.[0]?.userId == undefined}
                                    onChange={userId => {
                                        if (userId) {
                                            setValue(`agents.1.userId`, userId)
                                        } else {
                                            removeAgent(1)
                                        }
                                    }}
                                />
                            </FormControl>
                            <Controller
                                name="lotNumber"
                                disabled={formDisabled}
                                control={control}
                                render={field => (
                                    <SlotInput
                                        label="Lot Number"
                                        type="textsmall"
                                        {...field}
                                    />
                                )}
                            />
                            <Controller
                                name="planNumber"
                                disabled={formDisabled}
                                control={control}
                                render={field => (
                                    <SlotInput
                                        label="Plan Number"
                                        type="textsmall"
                                        {...field}
                                    />
                                )}
                            />
                            <Controller
                                name="titleReference"
                                disabled={formDisabled}
                                control={control}
                                render={field => (
                                    <SlotInput
                                        label="Title Reference"
                                        type="textsmall"
                                        {...field}
                                    />
                                )}
                            />
                            <QueryQldGovDataButton
                                listingAddress={listingAddress || null}
                                onDataFound={data => {
                                    console.log('QLD data found:', data)
                                    // You can handle the data here, e.g., set it to state or show in a modal
                                }}
                            />
                        </Stack>
                        {/*<Box
                        sx={{
                            display: 'flex', justifyContent: 'flex-end'
                        }}
                    >
                        <Box sx={{ mb: 2 }}>
                            <Button
                                disabled
                                color='success'
                                loading={loadingCL}
                                onClick={async () => {
                                    setLoadingCL(true)
                                    try {
                                        const { data } = await api.post('/corelogic/propertyfromaddress', {
                                            address: listing.listingAddress
                                        })
                                        console.log('data', data)

                                        if (data.legal.parcels.length == 1) {
                                            setValue('lotNumber', data.legal.parcels[0].lot)
                                            setValue('planNumber', data.legal.parcels[0].plan)
                                            setValue('titleReference', data.legal.title.titleReference)
                                        }
                                    } catch (e) {
                                        showSnack('Error getting CoreLogic info', 'danger')
                                    } finally {
                                        setLoadingCL(false)
                                    }
                                }}
                            >
                                Get Core Logic Info
                            </Button>
                            <Typography>*Not available until prod</Typography>
                        </Box>
                    </Box>*/}
                        {type == 'sale' && (
                            <>
                                <Divider />
                                <VendorList
                                    name="Vendor"
                                    //showAlert={(sensitiveInfo?.replaceAll(' ', '').length ?? 0) > 0}
                                    disabled={formDisabled}
                                    vendors={vendors}
                                    removeVendor={removeVendor}
                                    vendorType={vendorType}
                                    setVendorType={setVendorType}
                                    showOccupantStatus={true}
                                    showSensitiveInfo
                                    extraFields={(vendor, i) =>
                                        occupantStatus == 'investment' &&
                                        vendorType == 'individual' && (
                                            <LocationAutocomplete
                                                name={`vendors.${i}.address`}
                                                control={control}
                                                label="Address"
                                                slotProps={{
                                                    input: {
                                                        tabIndex: 0,
                                                    },
                                                }}
                                            />
                                        )
                                    }
                                />
                                {vendors.length < 6 && !formDisabled && editMode && (
                                    <Box>
                                        <Button
                                            variant="outlined"
                                            onClick={() =>
                                                appendVendor({
                                                    vendorType: vendorType as any,
                                                    firstName: '',
                                                    lastName: '',
                                                    email: '',
                                                    mobileNumber: '',
                                                    abn: '',
                                                })
                                            }
                                        >
                                            Add Vendor
                                        </Button>
                                    </Box>
                                )}
                                <Divider />
                                <Typography level="h4">Pricing</Typography>
                                <Stack
                                    gap={2}
                                    maxWidth={500}
                                >
                                    <Controller
                                        name="listingType"
                                        disabled={formDisabled}
                                        control={control}
                                        render={field => (
                                            <SlotWrapper
                                                {...field}
                                                label="Listing Type"
                                            >
                                                {editMode ? (
                                                    <RadioGroup orientation="horizontal">
                                                        <Radio
                                                            value="sale"
                                                            label="Sale"
                                                            checked={field.field.value == 'sale'}
                                                            disabled={formDisabled}
                                                        />
                                                        <Radio
                                                            value="auction"
                                                            label="Auction"
                                                            checked={field.field.value == 'auction'}
                                                            disabled={formDisabled}
                                                        />
                                                    </RadioGroup>
                                                ) : (
                                                    <Typography>{capitalize(field.field.value)}</Typography>
                                                )}
                                            </SlotWrapper>
                                        )}
                                    />
                                    {listingType == 'auction' && (
                                        <Controller
                                            name="auctionDate"
                                            disabled={formDisabled}
                                            control={control}
                                            render={field => (
                                                <SlotInput
                                                    {...field}
                                                    label="Auction Date"
                                                    type="date"
                                                />
                                            )}
                                        />
                                    )}
                                    <Controller
                                        name="commission"
                                        disabled={formDisabled}
                                        control={control}
                                        render={field => (
                                            <SlotInput
                                                label="Commission Rate Inclusive"
                                                type="number"
                                                {...field}
                                                endDecorator="%"
                                                formControlProps={{
                                                    sx: { flex: 1 },
                                                }}
                                                slotProps={{
                                                    input: {
                                                        step: 0.1,
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="advertisedPrice"
                                        disabled={formDisabled}
                                        control={control}
                                        render={field => (
                                            <SlotInput
                                                label={
                                                    listingType == 'auction' ? 'Reserve' : 'Advertised Price'
                                                }
                                                type="textsmall"
                                                {...field}
                                                formControlProps={
                                                    {
                                                        //sx: { flex: 1 },
                                                    }
                                                }
                                                //startDecorator="$"
                                                //type="dollar"
                                            />
                                        )}
                                    />
                                    <Input
                                        label="Est Commission Amount Inc"
                                        value={
                                            commissionCalculatedPrice && commission
                                                ? Math.round((commissionCalculatedPrice * commission) / 100)
                                                : ''
                                        }
                                        readOnly
                                        disabled
                                        formControlProps={{
                                            sx: { flex: 1 },
                                        }}
                                        startDecorator="$"
                                        type="dollar"
                                    />
                                    <Controller
                                        name="hiddenPrice.type"
                                        disabled={formDisabled}
                                        control={control}
                                        render={field => (
                                            <SlotWrapper
                                                {...field}
                                                label="Search Price Type"
                                            >
                                                {editMode ? (
                                                    <RadioGroup orientation="horizontal">
                                                        <Radio
                                                            value="fixed"
                                                            label="Fixed"
                                                            checked={field.field.value == 'fixed'}
                                                            disabled={formDisabled}
                                                        />
                                                        <Radio
                                                            value="range"
                                                            label="Range"
                                                            checked={field.field.value == 'range'}
                                                            disabled={formDisabled}
                                                        />
                                                    </RadioGroup>
                                                ) : (
                                                    <Typography>{capitalize(field.field.value)}</Typography>
                                                )}
                                            </SlotWrapper>
                                        )}
                                    />
                                    {searchPriceType == 'fixed' && (
                                        <Controller
                                            disabled={formDisabled}
                                            name="hiddenPrice.amount"
                                            control={control}
                                            render={field => (
                                                <SlotInput
                                                    label="Search Price"
                                                    type="dollar"
                                                    {...field}
                                                    formControlProps={{
                                                        sx: { flex: 1 },
                                                    }}
                                                    startDecorator="$"
                                                />
                                            )}
                                        />
                                    )}
                                    {searchPriceType == 'range' && (
                                        <MobileFriendlyStack>
                                            <Controller
                                                disabled={formDisabled}
                                                name="hiddenPrice.min"
                                                control={control}
                                                render={field => (
                                                    <SlotInput
                                                        label="Min"
                                                        type="dollar"
                                                        {...field}
                                                        formControlProps={{
                                                            sx: { flex: 1 },
                                                        }}
                                                        startDecorator="$"
                                                    />
                                                )}
                                            />
                                            <Controller
                                                name="hiddenPrice.max"
                                                disabled={formDisabled}
                                                control={control}
                                                render={field => (
                                                    <SlotInput
                                                        label="Max"
                                                        type="dollar"
                                                        {...field}
                                                        formControlProps={{
                                                            sx: { flex: 1 },
                                                        }}
                                                        startDecorator="$"
                                                    />
                                                )}
                                            />
                                        </MobileFriendlyStack>
                                    )}
                                </Stack>
                            </>
                        )}
                        <Divider />
                        <Typography level="h4">Authority</Typography>
                        <Stack
                            gap={2}
                            maxWidth={500}
                        >
                            {editMode ? (
                                <>
                                    <Controller
                                        name="authorityStartDate"
                                        disabled={formDisabled}
                                        control={control}
                                        render={field => (
                                            <SlotInput
                                                label="Authority Start Date"
                                                type="date"
                                                {...field}
                                                formControlProps={{
                                                    sx: { flex: 1 },
                                                }}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="authorityPeriod"
                                        control={control}
                                        disabled={formDisabled}
                                        render={field => (
                                            <SlotInput
                                                label="Authority Duration"
                                                type="number"
                                                slotProps={{
                                                    input: {
                                                        max: 90,
                                                        min: 1,
                                                    },
                                                }}
                                                {...field}
                                                formControlProps={{
                                                    sx: { flex: 1 },
                                                }}
                                                endDecorator="days"
                                            />
                                        )}
                                    />
                                    <Input
                                        label="Authority End Date"
                                        type="date"
                                        value={
                                            authorityStartDate && authorityPeriod
                                                ? format(
                                                      addDays(
                                                          new Date(authorityStartDate),
                                                          authorityPeriod - 1,
                                                      ),
                                                      'yyyy-MM-dd',
                                                  )
                                                : undefined
                                        }
                                        readOnly
                                        disabled
                                        formControlProps={{
                                            sx: { flex: 1 },
                                        }}
                                    />
                                </>
                            ) : (
                                <Typography>
                                    {authorityStartDate && authorityPeriod
                                        ? `${format(new Date(authorityStartDate), 'dd/MM/yyyy')} - ${format(
                                              addDays(new Date(authorityStartDate), authorityPeriod - 1),
                                              'dd/MM/yyyy',
                                          )}`
                                        : 'None'}
                                </Typography>
                            )}
                        </Stack>
                        <Divider />
                        <Typography level="h4">Marketing Package</Typography>
                        <Stack
                            direction="row"
                            gap={4}
                            sx={{ alignItems: 'center' }}
                        >
                            {marketingPackage && (
                                <Box>
                                    <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                                        <Typography>
                                            <b>{marketingPackage.packageName}</b>
                                        </Typography>
                                        <Typography>
                                            <b>
                                                {toDollarAmount(
                                                    sum(marketingPackage.packageItems.map(prop('itemPrice'))),
                                                )}
                                            </b>
                                        </Typography>
                                        {editMode && !formDisabled && (
                                            <IconButton
                                                size="sm"
                                                variant="outlined"
                                                onClick={() => setShowMarketingPackageModal(true)}
                                                disabled={
                                                    formDisabled ||
                                                    (listing.marketingPaymentStatus != undefined &&
                                                        !(
                                                            listing.marketingPaymentStatus?.paymentStatus ==
                                                            'cancelled'
                                                        ))
                                                }
                                            >
                                                <EditRounded />
                                            </IconButton>
                                        )}
                                    </Box>
                                    {editMode ? (
                                        <Stack gap={2}>
                                            {marketingPackage.packageItems.map((item, i) => (
                                                <Stack
                                                    key={i}
                                                    direction="row"
                                                    gap={2}
                                                >
                                                    <Controller
                                                        name={`marketingPackage.packageItems.${i}.itemName`}
                                                        control={control}
                                                        render={({ field }) => <Input {...field} />}
                                                    />
                                                    <Controller
                                                        name={`marketingPackage.packageItems.${i}.itemPrice`}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                {...field}
                                                                type="dollar"
                                                            />
                                                        )}
                                                    />
                                                    {!formDisabled && (
                                                        <IconButton
                                                            size="sm"
                                                            variant="outlined"
                                                            onClick={() => {
                                                                marketingPackageItems.remove(i)
                                                            }}
                                                            disabled={formDisabled}
                                                        >
                                                            <DeleteRounded />
                                                        </IconButton>
                                                    )}
                                                </Stack>
                                            ))}
                                        </Stack>
                                    ) : (
                                        <Table sx={{ maxWidth: 400, tableLayout: 'auto' }}>
                                            <>
                                                {marketingPackage.packageItems.map((item, i) => (
                                                    <tr>
                                                        <td
                                                            style={{
                                                                minWidth: 'fit-content',
                                                            }}
                                                        >
                                                            {item.itemName}
                                                        </td>
                                                        <td
                                                            align="right"
                                                            style={{
                                                                minWidth: 'fit-content',
                                                            }}
                                                        >
                                                            ${item.itemPrice}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </>
                                        </Table>
                                    )}
                                    {editMode && !formDisabled && (
                                        <Button
                                            variant="outlined"
                                            sx={{ mt: 3 }}
                                            onClick={() => setShowMarketingItemsModal(true)}
                                        >
                                            Add Item
                                        </Button>
                                    )}
                                </Box>
                            )}
                            {editMode && !formDisabled && !marketingPackage && (
                                <Box>
                                    <Button
                                        variant="outlined"
                                        onClick={() => setShowMarketingPackageModal(true)}
                                        disabled={formDisabled}
                                    >
                                        Add Package
                                    </Button>
                                </Box>
                            )}
                        </Stack>
                        {marketingPackage &&
                            sum(marketingPackage.packageItems.map(prop('itemPrice'))) > 0 && (
                                <Stack
                                    direction="row"
                                    sx={{
                                        gap: 2,
                                        alignItems: 'flex-end',
                                    }}
                                >
                                    <Controller
                                        name="marketingPaymentMethod"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControl>
                                                <FormLabel>Payment Method</FormLabel>
                                                <Select
                                                    {...field}
                                                    onChange={(e, value) => field.onChange(value)}
                                                    disabled={
                                                        formDisabled ||
                                                        (listing.marketingPaymentStatus != undefined &&
                                                            listing.marketingPaymentStatus?.paymentStatus !=
                                                                'cancelled')
                                                    }
                                                >
                                                    <Option value="rello">Rello</Option>
                                                    <Option value="settlement">On Settlement</Option>
                                                    <Option value="manual">Managed Externally</Option>
                                                </Select>
                                            </FormControl>
                                        )}
                                    />
                                    {marketingPaymentMethod == 'rello' && (
                                        <RelloMarketingPayment
                                            saveListing={async () => {
                                                if (formState.isDirty) {
                                                    if (await trigger()) {
                                                        //await handleSubmit(onSubmit)()
                                                        await saveForm()
                                                        return true
                                                    } else {
                                                        return false
                                                    }
                                                }
                                                return true
                                            }}
                                        />
                                    )}
                                    <CreateXeroMarketingInvoiceButton />
                                </Stack>
                            )}
                        <Divider />
                        <Typography level="h4">Additional Charges</Typography>
                        <Box>
                            <Table
                                sx={{
                                    width: editMode ? '1000px' : '600px',
                                    '& td': {
                                        borderBottom: 'none !important',
                                    },
                                }}
                            >
                                <tbody>
                                    <tr>
                                        <td>
                                            <FormLabel>Description</FormLabel>
                                        </td>
                                        <td>
                                            <FormLabel>Amount</FormLabel>
                                        </td>
                                        <td>
                                            <FormLabel>Pay on</FormLabel>
                                        </td>
                                        <td>
                                            <FormLabel>Payable Date</FormLabel>
                                        </td>
                                        {editMode && <td></td>}
                                    </tr>
                                    {additionalCharges.map((charge, i) => (
                                        <AdditionalChargeItem
                                            key={i}
                                            charge={charge}
                                            index={i}
                                            removeAdditionalCharge={removeAdditionalCharge}
                                            formDisabled={formDisabled}
                                        />
                                    ))}
                                </tbody>
                            </Table>
                        </Box>
                        {additionalCharges.length < 4 && !formDisabled && editMode && (
                            <Box sx={{ mt: 2 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() =>
                                        appendAdditionalCharge({
                                            description: '',
                                            amount: 0,
                                            payType: 'now',
                                            payable: format(new Date(), 'yyyy-MM-dd'),
                                        })
                                    }
                                >
                                    Add Charge
                                </Button>
                            </Box>
                        )}
                        {false && user.superUser && user.lastName == 'Bleier' && (
                            <Box>
                                <Button
                                    color="danger"
                                    loading={ocrLoading}
                                    onClick={async () => {
                                        setOcrLoading(true)
                                        await api.post(
                                            `/listing/${listingType}/${listing.listingId}/agentappointmentform/azureocr`,
                                        )
                                        setOcrLoading(false)
                                    }}
                                >
                                    Azure OCR
                                </Button>
                            </Box>
                        )}
                        {!isNew && (
                            <>
                                <Divider />
                                <Box>
                                    <FormStepper
                                        title="Form 6"
                                        formType="agentAppointmentForm"
                                        createDisabled={
                                            vendors.length == 0 ||
                                            listing.agents.length == 0 ||
                                            formFullyDisabled
                                        }
                                        validate={async () => {
                                            const start = Date.now()
                                            const requiredValues: Array<keyof UpdateSaleListingSchema> = [
                                                'authorityStartDate',
                                                'authorityPeriod',
                                                'lotNumber',
                                                'planNumber',
                                                //'titleReference',
                                                //'advertisedPrice',
                                                'hiddenPrice',
                                            ]

                                            var shouldFocus = true

                                            const block = await Promise.all(
                                                requiredValues.map(async reqVal => {
                                                    const error = getValues(reqVal) == undefined

                                                    if (error) {
                                                        setError(
                                                            reqVal,
                                                            {
                                                                type: 'required',
                                                                message: 'Required for Form 6',
                                                            },
                                                            {
                                                                shouldFocus,
                                                            },
                                                        )

                                                        shouldFocus = false
                                                        console.log('error', reqVal)
                                                    }
                                                    return error
                                                }),
                                            )
                                            if (block.some(x => x == true)) {
                                                console.log('block time', Date.now() - start)
                                                return false
                                            }
                                            return await trigger()
                                        }}
                                        preCreate={async () => {
                                            if (formState.isDirty) {
                                                return await saveForm()
                                                //return true
                                            } else {
                                                return true
                                            }
                                        }}
                                    />
                                </Box>
                            </>
                        )}
                        <Divider />
                        <Box sx={{ mt: 2, mb: 2 }}>
                            <RenderListingValues
                                tab="listing"
                                disabled={formFullyDisabled}
                            />
                        </Box>
                    </Stack>
                    {/*<Box>
                        <Button
                            color="danger"
                            onClick={async () => {
                                await api.post(`/listing/${listing.listingId}/move/create`)
                            }}
                            sx={{ mt: 8 }}
                        >
                            SEND TO MOVE
                        </Button>
                    </Box>*/}
                    <MarketingPackageModal
                        open={showMarketingPackageModal}
                        close={() => setShowMarketingPackageModal(false)}
                        setValue={setValue}
                        currentPackage={marketingPackage}
                    />
                    <MarketingItemsModal
                        open={showMarketingItemsModal}
                        close={() => setShowMarketingItemsModal(false)}
                        setValue={setValue}
                        currentPackage={marketingPackage}
                    />
                    <UnsavedModal
                        blocker={blocker}
                        save={(onSuccess, onFail) =>
                            /*handleSubmit(async x => {
                                await onSubmit(x)
                                onSuccess()
                            }, onFail)()*/
                            saveForm().then(x => (x ? onSuccess() : onFail()))
                        }
                    />
                </form>

                <Modal
                    open={maybeQbccInfo.isSome()}
                    onClose={() => setQbccInfo(undefined)}
                >
                    <Sheet
                        variant="outlined"
                        sx={{
                            p: 4,
                            m: 8,
                        }}
                    >
                        {maybeQbccInfo.map(qbccInfo => (
                            <Stack spacing={2}>
                                <Typography sx={{ color: 'red' }}>
                                    <b>Number of Pools:</b> {qbccInfo['Number of Pools']}
                                </Typography>
                                <Typography>
                                    <b>Shared Pool Property:</b> {qbccInfo['Shared Pool Property']}
                                </Typography>
                                <Typography sx={{ color: 'red' }}>
                                    <b>Site Name:</b> {qbccInfo['Site Name']}
                                </Typography>
                                <Typography>
                                    <b>Street Name:</b> {qbccInfo['Street Name']}
                                </Typography>
                                <Typography>
                                    <b>Street Number:</b> {qbccInfo['Street Number']}
                                </Typography>
                                <Typography>
                                    <b>Street Type:</b> {qbccInfo['Street Type']}
                                </Typography>
                                <Typography>
                                    <b>Suburb:</b> {qbccInfo['Suburb']}
                                </Typography>
                                <Typography>
                                    <b>Unit Number:</b> {qbccInfo['Unit Number']}
                                </Typography>
                                <Typography sx={{ color: 'red' }}>
                                    <b>Has Certificate:</b>{' '}
                                    {maybePoolCertificate
                                        .map(cert => (cert == false ? 'No' : `Yes, submitted ${cert}`))
                                        .orSome('Loading...')}
                                </Typography>
                            </Stack>
                        ))}
                    </Sheet>
                </Modal>
            </FormProvider>
        </Box>
    )
})

export default CoreListingDetails
